import React from 'react';
import { FILTER_ALL, MAPPING_STATUS, normalizeMappingStatus } from './constants';
import { upsertMappings } from './indexes';

/** Last segment of a dotted attribute path. */
export function getAttributeLeafName(attribute) {
    const value = String(attribute || '');
    if (!value) return '';
    const parts = value.split('.');
    return parts[parts.length - 1] || value;
}

/** Group mappings by KPI / model id for O(1) row joins. */
export function indexMappingsByEntity(mappings = []) {
    const byKpiId = new Map();
    const byModelId = new Map();
    mappings.forEach((m) => {
        if (m.kpiId != null) {
            const key = String(m.kpiId);
            if (!byKpiId.has(key)) byKpiId.set(key, []);
            byKpiId.get(key).push(m);
        }
        if (m.modelId != null) {
            const key = String(m.modelId);
            if (!byModelId.has(key)) byModelId.set(key, []);
            byModelId.get(key).push(m);
        }
    });
    return { byKpiId, byModelId };
}

export function getMappingsForEntity(index, itemId, isModel = false) {
    if (!index || itemId == null) return [];
    const map = isModel ? index.byModelId : index.byKpiId;
    return map.get(String(itemId)) || [];
}

/**
 * Highlight matching search substrings — same look as Realtime left-tree screenshot:
 * pale peach background on the match; inherited text color/weight.
 */
export function getHighlightedSearchText(text, highlight) {
    const safeText = String(text ?? '');
    const safeHighlight = String(highlight ?? '').trim();
    if (!safeHighlight) return safeText;
    if (safeHighlight.length > 100) return safeText;

    const escapedHighlight = safeHighlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = safeText.split(new RegExp(`(${escapedHighlight})`, 'gi'));

    return parts.map((part, i) => (
        <span
            key={`${i}-${part}`}
            className={part.toLowerCase() === safeHighlight.toLowerCase() ? 'configure-search-highlight' : undefined}
        >
            {part}
        </span>
    ));
}

/** Coverage metrics for Data Assets Mapping header / info popup. */
export function computeMappingMetrics(mappings = [], kpis = [], isKpiEff = () => true) {
    const activeKpis = kpis.filter(isKpiEff);
    const totalActiveKpis = activeKpis.length;

    if (totalActiveKpis === 0) {
        return {
            mappedPercent: 0,
            autoCount: 0,
            manualCount: 0,
            totalActiveKpis: 0,
            mappedCount: 0,
            unmappedCount: 0,
            totalMappings: 0
        };
    }

    const enabled = mappings.filter(m => m.enabled !== false && m.kpiId);
    const enabledKpiIds = new Set(enabled.map(m => m.kpiId));
    const mappedCount = activeKpis.reduce((n, k) => n + (enabledKpiIds.has(k.id) ? 1 : 0), 0);
    const unmappedCount = totalActiveKpis - mappedCount;
    const mappedPercent = Math.round((mappedCount / totalActiveKpis) * 100);

    let autoCount = 0;
    let manualCount = 0;
    enabled.forEach(m => {
        if (normalizeMappingStatus(m.status) === MAPPING_STATUS.MANUAL) manualCount += 1;
        else autoCount += 1;
    });

    return {
        mappedPercent,
        autoCount,
        manualCount,
        totalActiveKpis,
        mappedCount,
        unmappedCount,
        totalMappings: autoCount + manualCount
    };
}

/** Pad QualdoDataTable rows to a minimum visible length. */
export function padTableRows(formattedRows, { targetLimit = 15, columnCount = 6 } = {}) {
    const rows = [...formattedRows];
    const realRowsCount = rows.length;
    if (realRowsCount === 0 || realRowsCount > targetLimit) {
        return { rows, realRowsCount };
    }

    const padNeeded = (targetLimit + 1) - realRowsCount;
    for (let i = 0; i < padNeeded; i += 1) {
        const dummyRow = { id: `dummy_pad_${i}`, isDummy: true };
        const cells = [];
        for (let c = 0; c < columnCount; c += 1) {
            cells.push({
                type: c === columnCount - 1 ? 'component' : 'td',
                value: '',
                row: dummyRow
            });
        }
        rows.push(cells);
    }
    return { rows, realRowsCount };
}

export async function copyTextToClipboard(text) {
    if (!text) return false;
    try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch (e) {
        // fall through to execCommand
    }

    try {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand('copy');
        document.body.removeChild(textarea);
        return ok;
    } catch (e) {
        return false;
    }
}

export function toIdSet(ids) {
    return new Set(Array.isArray(ids) ? ids : [ids].filter(Boolean));
}

/** Normalize a single id or id list into a plain array. */
export function toIdList(idOrIds) {
    if (Array.isArray(idOrIds)) return idOrIds.filter(Boolean);
    return idOrIds != null && idOrIds !== '' ? [idOrIds] : [];
}

/** True when the event target is outside all provided DOM nodes. */
export function isClickOutside(event, ...nodes) {
    const target = event && event.target;
    if (!target) return false;
    return nodes.every((node) => !node || !node.contains(target));
}

/** True when two ids match after string normalization. */
export function sameId(a, b) {
    return a != null && b != null && String(a) === String(b);
}

/** Stable bulk-selection key for catalogue / mapping rows. */
export function entitySelectionKey(type, id) {
    return `${type}:${String(id)}`;
}

/** Set membership that works across numeric/string ids. */
export function filterSetHas(set, id) {
    if (!set || !set.size || id == null) return false;
    const key = String(id);
    for (const item of set) {
        if (String(item) === key) return true;
    }
    return false;
}

/**
 * Filter set update (business, function, KPI multiselect).
 * Pass a full id array (replace selection) when checked is undefined.
 */
export function nextFilterSet(currentSet, idOrIds, checked) {
    if (Array.isArray(idOrIds) && checked === undefined) {
        return new Set(idOrIds);
    }
    const nextSet = new Set(currentSet);
    toIdList(idOrIds).forEach((id) => {
        if (checked) nextSet.add(id);
        else nextSet.delete(id);
    });
    return nextSet;
}

/** @deprecated use nextFilterSet */
export function nextBusinessFilterSet(currentSet, bizIdOrIds, checked) {
    return nextFilterSet(currentSet, bizIdOrIds, checked);
}

/** Functions visible for the current business filter. */
export function getScopedFunctions(functions = [], filterBusinesses, isFnEff = () => true) {
    if (!filterBusinesses || filterBusinesses.size === 0) {
        return functions.filter(isFnEff);
    }
    return functions.filter((f) => filterSetHas(filterBusinesses, f.businessId) && isFnEff(f));
}

/** KPIs visible for the current business/function filters. */
export function getScopedKpis(
    kpis = [],
    functions = [],
    filterBusinesses,
    filterFunctions = new Set(),
    isKpiEff = () => true
) {
    const fnFilter = filterFunctions || new Set();
    return kpis.filter((k) => {
        if (!isKpiEff(k)) return false;
        const fn = functions.find((f) => sameId(f.id, k.functionId));
        if (!fn) return false;
        if (filterBusinesses && filterBusinesses.size > 0 && !filterSetHas(filterBusinesses, fn.businessId)) {
            return false;
        }
        if (fnFilter.size > 0 && !filterSetHas(fnFilter, fn.id)) {
            return false;
        }
        return true;
    });
}

/** Union entity lists by id; later lists override overlapping fields. */
export function mergeEntityListById(...lists) {
    const map = new Map();
    lists.forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((item) => {
            if (item == null || item.id == null) return;
            const key = String(item.id);
            map.set(key, { ...(map.get(key) || {}), ...item });
        });
    });
    return Array.from(map.values());
}

/** Whether a KPI/model row should appear under current toolbar filters (incl. unmapped). */
export function kpiItemMatchesFilters(item, fn, biz, itemMappings = [], filters = {}, indexes, searchVal = '') {
    const {
        filterBusinesses,
        filterFunctions = new Set(),
        filterKpis = new Set(),
        filterStatus = FILTER_ALL
    } = filters;
    const bizFilter = filterBusinesses || new Set();
    const fnFilter = filterFunctions || new Set();
    const kpiFilter = filterKpis || new Set();

    if (bizFilter.size > 0 && biz && !filterSetHas(bizFilter, biz.id)) return false;
    if (fnFilter.size > 0 && fn && !filterSetHas(fnFilter, fn.id)) return false;
    if (kpiFilter.size > 0 && item && !filterSetHas(kpiFilter, item.id)) return false;

    if (filterStatus !== FILTER_ALL) {
        if (!itemMappings.length) return false;
        const hasStatus = itemMappings.some(
            (m) => normalizeMappingStatus(m.status) === filterStatus
        );
        if (!hasStatus) return false;
    }

    const q = String(searchVal || '').trim().toLowerCase();
    if (!q) return true;

    const simpleHaystack = [
        item && item.name,
        biz && biz.name,
        fn && fn.name,
        item && item.formula,
        item && item.kind
    ].filter(Boolean).join(' ').toLowerCase();
    if (simpleHaystack.includes(q)) return true;

    if (!itemMappings.length) return false;

    for (let i = 0; i < itemMappings.length; i += 1) {
        const m = itemMappings[i];
        const asset = indexes && indexes.getAsset ? indexes.getAsset(m.assetId) : null;
        const assetHaystack = [
            asset && asset.datasource,
            asset && asset.dataset,
            asset && asset.attribute,
            m.status
        ].filter(Boolean).join(' ').toLowerCase();
        if (assetHaystack.includes(q)) return true;
    }
    return false;
}

/** Search haystack for a mapping row (name, biz, fn, formula, asset path, status). */
export function mappingMatchesSearch(mapping, indexes, searchVal, resolvedCtx) {
    const q = String(searchVal || '').trim().toLowerCase();
    if (!q) return true;
    const ctx = resolvedCtx || indexes.resolveMappingContext(mapping);
    if (!ctx) return false;
    const asset = ctx.asset;
    const haystack = [
        ctx.item && ctx.item.name,
        ctx.biz && ctx.biz.name,
        ctx.fn && ctx.fn.name,
        ctx.kpi && ctx.kpi.formula,
        asset && asset.datasource,
        asset && asset.dataset,
        asset && asset.attribute,
        mapping.status
    ].filter(Boolean).join(' ').toLowerCase();
    return haystack.includes(q);
}

/** Active, filter-matching mappings for the Domain Mapping table. */
export function filterDomainMappings(mappings = [], filters = {}, indexes) {
    const {
        searchVal = '',
        filterBusinesses,
        filterFunctions = new Set(),
        filterKpis = new Set(),
        filterStatus = FILTER_ALL
    } = filters;
    const bizFilter = filterBusinesses || new Set();
    const fnFilter = filterFunctions || new Set();
    const kpiFilter = filterKpis || new Set();
    const q = String(searchVal || '').trim();

    return mappings.filter((m) => {
        const ctx = indexes.resolveMappingContext(m);
        if (!ctx || !ctx.kpi || !ctx.effectivelyActive) return false;

        const { biz, fn, kpi } = ctx;
        if (bizFilter.size > 0 && biz && !filterSetHas(bizFilter, biz.id)) return false;
        if (fnFilter.size > 0 && fn && !filterSetHas(fnFilter, fn.id)) return false;
        if (kpiFilter.size > 0 && kpi && !filterSetHas(kpiFilter, kpi.id)) return false;
        if (filterStatus !== FILTER_ALL && normalizeMappingStatus(m.status) !== filterStatus) {
            return false;
        }
        if (q && !mappingMatchesSearch(m, indexes, q, ctx)) return false;
        return true;
    });
}

/** Immutably set `enabled` on mappings whose ids are in mappingIds. */
export function withMappingsEnabled(mappings = [], mappingIds, enabled) {
    const idSet = toIdSet(mappingIds);
    if (!idSet.size) return mappings;
    return mappings.map((m) => (idSet.has(m.id) ? { ...m, enabled } : m));
}

/**
 * Resolve whether a toggle should enable or disable the selected group.
 * Returns null when the group is empty or not effectively active.
 */
export function resolveMappingToggleEnabled(mappings = [], mappingIdOrIds, indexes) {
    const ids = toIdList(mappingIdOrIds);
    if (!ids.length) return null;
    const idSet = toIdSet(ids);
    const group = mappings.filter((m) => idSet.has(m.id));
    if (!group.length) return null;
    const ctx = indexes.resolveMappingContext(group[0]);
    if (!ctx || !ctx.effectivelyActive) return null;
    return { ids, enabled: !group.every((m) => m.enabled) };
}

/** Drop mappings by id. */
export function withoutMappingsById(mappings = [], mappingIds) {
    const idSet = toIdSet(mappingIds);
    if (!idSet.size) return mappings;
    return mappings.filter((m) => !idSet.has(m.id));
}

/** Append assets referenced by saved mappings that are not already present. */
export function mergeAssetsFromMappings(assets = [], mappingsArray = []) {
    if (!mappingsArray.length) return assets;
    const nextAssets = [...assets];
    const seen = new Set(nextAssets.map((a) => a.id));
    mappingsArray.forEach((savedMapping) => {
        const asset = savedMapping && savedMapping.asset;
        if (asset && asset.id && !seen.has(asset.id)) {
            nextAssets.push(asset);
            seen.add(asset.id);
        }
    });
    return nextAssets;
}

/** Apply a formula update payload to the KPI list. */
export function applyKpiFormulaUpdate(kpis = [], formulaUpdate) {
    if (!formulaUpdate || !formulaUpdate.id) return kpis;
    return kpis.map((k) =>
        k.id === formulaUpdate.id ? { ...k, formula: formulaUpdate.formula } : k
    );
}

/**
 * Normalize AddMappingModal save payloads into a consistent shape.
 * Supports legacy array payloads and object payloads with replace/formula fields.
 */
export function parseSaveMappingPayload(payload, editingMapping = null) {
    const isArray = Array.isArray(payload);
    const mappingsArray = isArray ? payload : (payload.mappings || []);
    const formulaUpdate = isArray ? null : payload.formulaUpdate;
    const replaceKpiId = isArray ? null : (payload.replaceKpiId || null);
    const replaceMappingId = isArray
        ? null
        : (replaceKpiId
            ? null
            : (payload.replaceMappingId || (editingMapping && editingMapping.id) || null));

    return { mappingsArray, formulaUpdate, replaceKpiId, replaceMappingId };
}

/** Build entity partials for persisting a mapping save. */
export function buildSaveMappingEntities(prevState, payload, editingMapping = null) {
    const {
        mappingsArray,
        formulaUpdate,
        replaceKpiId,
        replaceMappingId
    } = parseSaveMappingPayload(payload, editingMapping);

    return {
        mappings: upsertMappings(prevState.mappings, mappingsArray, {
            replaceMappingId,
            replaceKpiId
        }),
        assets: mergeAssetsFromMappings(prevState.assets, mappingsArray),
        kpis: applyKpiFormulaUpdate(prevState.kpis, formulaUpdate)
    };
}
